import { atom } from 'recoil';
import { getRecoil, setRecoil } from 'recoil-nexus';

import history from 'history/browser';
import { Account } from '../accounts/Account';
import { enumKeys, Region } from '../platformapi/Region';
import SettingsManager from '../settings/SettingsManager';
import { addToast, Toast, ToastType } from './ToastAtom';

interface AccountData {
	token: string;
	region: Region;
	userId?: string;
}

interface IOauthState {
	region?: Region;
	location?: string;
}

const SETTING_SELECTED_ACCOUNT = 'selected-account';
const SETTING_ACCOUNT_LIST = 'accounts';
const SETTING_CODE_VERIFIER = 'code-verifier';

/*** ATOMS ***/

export const accountsAtom = atom({
	key: SETTING_ACCOUNT_LIST,
	default: init(),
	effects_UNSTABLE: [
		({ onSet }) => {
			onSet((newAccountList) => {
				// Save the account list whenever it changes
				saveAccountList(newAccountList);
			});
		},
	],
});

export const selectedAccountAtom = atom({
	key: SETTING_SELECTED_ACCOUNT,
	default: undefined as unknown as Account | undefined,
});

/*** ATOM FUNCTIONS ***/

// removeAccount removes the provided account from the list of accounts
export function removeAccount(account: Account) {
	let newAccounts = getAccountList();
	const idx = newAccounts.indexOf(account);
	if (idx >= 0) {
		newAccounts.splice(idx, 1);
		setRecoil(accountsAtom, newAccounts);
	}
	let selectedAccount = getRecoil(selectedAccountAtom);
	if (selectedAccount?.userId === account.userId) {
		// console.log('setting default account', newAccounts);
		setSelectedAccount(newAccounts.length > 0 ? newAccounts[0] : undefined);
	}
}

export function setSelectedAccount(account?: Account) {
	setRecoil(selectedAccountAtom, account);
	saveSelectedAccount(account);
}

/*** PUBLIC NON-ATOM FUNCTIONS ***/
// authorizeNewAccount initiates a PKCE OAuth flow to get an access token

export async function authorizeNewAccount(region: Region, forcePromptForLogin = false) {
	try {
			// developer_tools built-in client
		const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID;

		if(!clientId) throw new Error('Client ID is not set');

		// Normalize redirect URI
		let redirectUri = window.location.origin;
		if (!redirectUri.endsWith('/')) redirectUri += '/';
		const codeVerifier = generatePKCECCodeVerifier();
		const codeChallenge = await encodePKCECodeVerifier(codeVerifier);

		// save code verifier to local storage
		sessionStorage.setItem(SETTING_CODE_VERIFIER, codeVerifier);

		window.location.href =
		`https://login.${encodeURIComponent(region)}/oauth/authorize?` +
		`client_id=${encodeURIComponent(clientId)}&` +
		`response_type=code&` +
		`redirect_uri=${encodeURIComponent(redirectUri)}&` +
		`code_challenge=${encodeURIComponent(codeChallenge)}&` +
		`code_challenge_method=S256`+
		`&state=${encodeURIComponent(serializeOauthState(region))}`+
		`${forcePromptForLogin ? '&prompt=login' : ''}`
		;
		
	} catch (error) {
		sessionStorage.removeItem(SETTING_CODE_VERIFIER);
		console.error(error);
		addToast({
			title: 'Error',
			message: 'Authorization process failed. Please try again or Contact Support by leaving a feedback message',
			toastType: ToastType.Critical,
		});
	}
}

/*** PRIVATE FUNCTIONS ***/

function getAccountList() {
	let accounts = getRecoil(accountsAtom);
	if (!accounts || typeof accounts[Symbol.iterator] !== 'function') return [];
	return [...accounts];
}



// init is used as a constructor to load accounts from storage when the account list atom is initialized. Valid accounts will be added to the list via state updates, not returned here.
async function init(): Promise<Account[]> {
	// Load accounts from storage and run the initializer on each one
	let accountDataList = ((await SettingsManager.getDirect(SETTING_ACCOUNT_LIST)) || []) as AccountData[];

	// Asynchronously initialize accounts
	const promises = [] as Promise<Account | undefined>[];
	accountDataList.forEach((accountData: AccountData) => promises.push(initAccount(accountData)));
	
	//fetch access token 
	const codeVerifier: string = sessionStorage.getItem(SETTING_CODE_VERIFIER) || '';
	const authCode: string | null = new URLSearchParams(window.location.search).get('code');
	const clientId: string | undefined = process.env.REACT_APP_OAUTH_CLIENT_ID;

	// Handle new login
	if (codeVerifier && authCode && clientId) {
		try {
			const state = deserializeOauthState();
			validateOauthState(state);
			const access_token = await fetchAccessToken(codeVerifier, state.region || Region.us_east_1, authCode, clientId);

			if (!!access_token && !!state) {
				//remove code verifier 
				sessionStorage.removeItem(SETTING_CODE_VERIFIER);
				promises.push(initAccount({ token: access_token, region: state.region || Region.us_east_1 }, true));
				if (state.location) {
					// Navigate internally within the app
					history.replace(state.location);
				} else {
					history.replace('/')
				}
				

				// Storage not allowed, warn users their setting will be lost
				setTimeout(() => {
					if (!SettingsManager.getStorageAllowed()) {
						const toast: Toast = {
							toastType: ToastType.Warning,
							title: 'Browser storage is disabled',
							message: `Accounts added to the account switcher will not be remembered when you refresh the page. Please enable browser storage in the Account Switcher above to allow your accounts to be remembered.`,
							timeoutSeconds: 30,
						};
						addToast(toast);
					}
				}, 5);
			} else {
				throw new Error('missing one or more required parameters for authorization');
			}
		} catch (error) {
			sessionStorage.removeItem(SETTING_CODE_VERIFIER);
			history.replace('/')
			console.error(error); 
			addToast({
				title: 'Error',
				message: 'Authorization process failed. Please try again or Contact Support by leaving a feedback message',
				toastType: ToastType.Critical,
			});
		}
	}

	// Set list of initialized accounts
	Promise.allSettled(promises).then(async (accounts) => {
		let selectedAccountId = ((await SettingsManager.getDirect(SETTING_SELECTED_ACCOUNT)) || []) as string | undefined;

		// Reduce to final list of accounts
		const filteredAccounts = scrubDuplicateAccounts(
			accounts.map((p) => (p.status === 'fulfilled' ? p.value : undefined)).filter((a) => a !== undefined) as Account[]
		);
		setRecoil(accountsAtom, filteredAccounts);

		// Ensure selected account is set
		const foundSelectedAccount = filteredAccounts.some((account) => {
			if (account.userId === selectedAccountId) {
				setSelectedAccount(account);
				return true;
			}
			return false;
		});
		if (!foundSelectedAccount && accounts.length > 0) {
			setSelectedAccount(filteredAccounts[0]);
		}
	});

	return [];
}

function serializeOauthState(region?: Region) {
	let state = `location=${encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)}`;
	if (region) {
		state += `&region=${encodeURIComponent(region)}`;
	}
	return encodeURIComponent(state);
}

function deserializeOauthState(): IOauthState {
	// Auth double encodes the returned state value, so we must double decode it
	return parseKeyValueString(decodeURIComponent(decodeURIComponent(parseKeyValueString(window.location.search).state || '')));
}

function validateOauthState(state: IOauthState) {
	//Will throw an error if region or location is invalid
	if(!state.region ||!enumKeys(Region).some(region => Region[region] === state.region)){
		throw new Error('Invalid region ' + state.region);
	}

	const locationRegex = /^\/[^/].*/;
	if (state.location && state.location === '/'){
		return;
	}

	if (!state.location || !locationRegex.test(state.location)) {
		throw new Error('Invalid location ' + state.location);
	}
}

function parseKeyValueString(s?: string) {
	if (!s) return {};
	// https://stackoverflow.com/a/5647103/14737424
	if (s.startsWith('?')) s = s.substring(1);
	let r = s
		.split('&')
		.map((v) => v.split('='))
		.reduce((pre, [key, value]) => ({ ...pre, [key]: decodeURIComponent(value) }), {}) as any;
	return r;
}

async function initAccount(accountData: AccountData, isNewAccount = false) {
	// Create account object and set cached id
	var newAccount = new Account(accountData.token, accountData.region);
	if (accountData.userId) newAccount.setUserId(accountData.userId);

	try {
		// Initialize account info
		await newAccount.init();
		if (isNewAccount) {
			addToast({
				title: 'Account added',
				message: `An account for ${newAccount.name} has been added in the ${newAccount.region} region. You can manage your active accounts using the account selector.`,
				toastType: ToastType.Success,
				timeoutSeconds: 20,
			});
		}

		return newAccount;
	} catch (err) {
		// Notify on error
		const msg = `Account in region ${newAccount.region} with userId ${newAccount.userId} has expired. Please add this account again to continue using it.`;
		console.error(msg, err);
		addToast({
			title: 'Account Error',
			message: msg,
			toastType: ToastType.Critical,
		});

		// Remove account from list
		//TODO: don't remove, show as invalid and prompt user to reauthorize
		removeAccount(newAccount);
	}
}

// Removes duplicate accounts and returns the scrubbed list
function scrubDuplicateAccounts(accounts: Account[]) {
	// console.log('scrubbing');
	const userIds = [] as string[];
	let keepAccounts = [] as Account[];

	// Run through the list backwards to keep newest accounts and accounts without user ids
	for (var i = accounts.length - 1; i >= 0; i--) {
		const userId = accounts[i].userId;
		if (userIds.includes(userId)) continue;
		if (userId && userId !== '') userIds.push(userId);
		keepAccounts.push(accounts[i]);
	}

	// Reverse the list to maintain the original order and notify
	keepAccounts.reverse();

	return keepAccounts;
}

// saveAccountList saves atom account data to local storage
function saveAccountList(accounts: Account[]) {
	//TODO: store org id so expired accounts can prompt to reauthorize in that org
	let accountData = [] as AccountData[];
	accounts.forEach((account: Account) => accountData.push(account.asData()));
	SettingsManager.setDirect(SETTING_ACCOUNT_LIST, accountData);
	// console.log('SAVED', accountData);
}

function saveSelectedAccount(account?: Account) {
	SettingsManager.setDirect(SETTING_SELECTED_ACCOUNT, account?.userId || '');
}



function generatePKCECCodeVerifier() {
	const verifiedChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
	const randomLengthBytes = new Uint8Array(1);

	window.crypto.getRandomValues(randomLengthBytes);
	const length = 43 + (randomLengthBytes[0] % 86);
	
	// Generate random bytes for each character position
	const randomBytes = new Uint8Array(length);
	window.crypto.getRandomValues(randomBytes);
	
	const codeVerifier = Array.from(randomBytes)
		.map((byte) => verifiedChars[byte % verifiedChars.length])
		.join('');
	
	return codeVerifier;
}



async function encodePKCECodeVerifier(codeVerifier: string) {
	const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
	const base64Hash = window.btoa(String.fromCharCode(...Array.from(new Uint8Array(hash))))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=/g, '');
	return base64Hash;
}

async function fetchAccessToken(codeVerifier: string, region: Region, authCode: string, clientId: string) {
	if(codeVerifier && region && authCode && clientId) {
		let redirectUri = window.location.origin;
		if (!redirectUri.endsWith('/')) {
			redirectUri += '/'
		}
		const authRes = await fetch(`https://login.${region}/oauth/token`, {
			method: 'POST',
			body: new URLSearchParams({
				'grant_type': 'authorization_code', 
				'code': authCode,
				'redirect_uri': redirectUri,
				'client_id': clientId,
				'code_verifier': codeVerifier,
				'code_challenge_method': 'S256',
			}).toString(),
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			}
		})
		if (authRes.status !== 200){
			throw new Error('Failed to fetch access token: ' + JSON.stringify(await authRes.json()));
		}
		const authResBody: OAuthTokenResponse = await authRes.json();
		if (authResBody.error) {
			throw new Error(`OAuth error: ${authResBody.error}`);
		}
		return authResBody.access_token;	
	} else {
		return null;
	}
}
interface OAuthTokenResponse {
	access_token: string;
	token_type: string;
	expires_in: number;
	refresh_token?: string;
	error?: string;
}